import React from 'react';
import {message} from 'antd';
import ModelerX6 from '@core_x6/lib/ModelerX6';
import {modelToJson} from '@core_x6/lib/export/JsonSerializer';
import {extractModelContent} from '@/utils/diagram/extractModelContent';
import {installCanvasContext} from '@/utils/diagram/canvasContext';

/** Кэш модели при размонтировании (Vite HMR): при ремаунте в течение TTL восстанавливаем модель из кэша вместо stale initialJson. Только для удобства разработки. */
const HMR_MODEL_CACHE_TTL_MS = 4000;
let hmrModelCache = null;

/**
 * Жизненный цикл ModelerX6, начальная загрузка модели, синхронизация списка views и патчей с коллаборацией.
 */
export function useArchiX6Modeler({
	initialXml,
	initialJson,
	initialDiagramName,
	initialViewId,
	onApplyPatch,
	onLive,
	onLock,
	clientSessionId,
	readOnly = false,
}) {
	const diagramContainerRef = React.useRef(null);
	const minimapContainerRef = React.useRef(null);
	const modelerRef = React.useRef(null);
	const patchSyncUnsubRef = React.useRef(null);
	const applyingRemotePatchRef = React.useRef(false);
	const hasInitializedRef = React.useRef(false);

	const [modelerReady, setModelerReady] = React.useState(false);
	const [viewReady, setViewReady] = React.useState(false);
	const [views, setViews] = React.useState([]);
	const [activeViewId, setActiveViewId] = React.useState(null);
	const [modelContent, setModelContent] = React.useState(null);

	const syncViews = React.useCallback(() => {
		const modeler = modelerRef.current;
		if (!modeler || !modeler.getViews) return;
		const list = modeler.getViews();
		setViews([...list]);
		const current = modeler.getActiveViewId?.() ?? null;
		const isValid = current && list.some(v => (v.id ?? v.$attrs?.id) === current);
		setActiveViewId(isValid ? current : (list[0]?.id ?? list[0]?.$attrs?.id) || null);

		const model = modeler.getModel?.();
		setModelContent(model ? extractModelContent(model) : null);
	}, []);

	React.useEffect(() => {
		if (!diagramContainerRef.current) return;
		const modeler = new ModelerX6({
			minimapContainer: minimapContainerRef.current ?? undefined,
			readOnly: Boolean(readOnly),
		});
		modelerRef.current = modeler;
		modeler.attachTo(diagramContainerRef.current);
		const detachCanvasContext = installCanvasContext(modeler);
		setModelerReady(true);

		return () => {
			detachCanvasContext();
			setModelerReady(false);
			setViewReady(false);
			patchSyncUnsubRef.current?.();
			patchSyncUnsubRef.current = null;
			const m = modeler.getModel?.();
			if (m) {
				const json = modelToJson(m);
				const viewId = modeler.getActiveViewId?.();
				hmrModelCache = {json, viewId, at: Date.now()};
			}
			modeler.disablePaletteBoxSelectMode?.();
			modeler.detach();
			modelerRef.current = null;
			hasInitializedRef.current = false;
		};
	}, [readOnly]);

	React.useEffect(() => {
		const modeler = modelerRef.current;
		if (!modeler || hasInitializedRef.current) return;

		const hasJson =
			initialJson != null &&
			(typeof initialJson === 'object' ||
				(typeof initialJson === 'string' && initialJson.trim()));
		const hasXml = initialXml != null && initialXml.trim();

		if (
			hmrModelCache &&
			HMR_MODEL_CACHE_TTL_MS > Date.now() - hmrModelCache.at &&
			!hasJson &&
			!hasXml
		) {
			const {json, viewId} = hmrModelCache;
			hmrModelCache = null;
			hasInitializedRef.current = true;
			modeler
				.importJSON(json)
				.then(() => modeler.openView(viewId ?? undefined))
				.then(() => {
					syncViews();
					setViewReady(true);
				})
				.catch(err => {
					hasInitializedRef.current = false;
					void message.error('Ошибка восстановления диаграммы после обновления');
					console.error(err);
				});
			return;
		}

		if (initialXml === undefined && initialJson === undefined) return;

		hasInitializedRef.current = true;

		const resolveViewToOpen = () => {
			const list = modeler.getViews?.() ?? [];
			const exists =
				initialViewId && list.some(v => (v.id ?? v.$attrs?.id) === initialViewId);
			return exists ? initialViewId : undefined;
		};

		if (hasJson) {
			modeler
				.importJSON(initialJson)
				.then(() => modeler.openView(resolveViewToOpen()))
				.then(() => {
					syncViews();
					setViewReady(true);
				})
				.catch(err => {
					hasInitializedRef.current = false;
					void message.error('Ошибка загрузки диаграммы');
					console.error(err);
				});
		} else if (hasXml) {
			modeler
				.importXML(initialXml)
				.then(() => modeler.openView(resolveViewToOpen()))
				.then(() => {
					syncViews();
					setViewReady(true);
				})
				.catch(err => {
					hasInitializedRef.current = false;
					void message.error('Ошибка загрузки диаграммы');
					console.error(err);
				});
		} else {
			modeler
				.createNewModel(initialDiagramName)
				.then(() => {
					syncViews();
					setViewReady(true);
				})
				.catch(err => {
					hasInitializedRef.current = false;
					void message.error('Ошибка создания модели');
					console.error(err);
				});
		}
	}, [initialXml, initialJson, initialDiagramName, initialViewId, syncViews]);

	React.useEffect(() => {
		const modeler = modelerRef.current;
		if (!modeler || !viewReady) return;
		const wrappedOnApplyPatch = patch => {
			if (applyingRemotePatchRef.current) return;
			if (onApplyPatch) onApplyPatch(patch);
			const hasModelLevelChange = patch?.patches?.some(
				p =>
					p?.op === 'addView' ||
					p?.op === 'updateViewName' ||
					p?.op === 'removeView' ||
					p?.op === 'addNode' ||
					p?.op === 'removeNode' ||
					p?.op === 'addConnection' ||
					p?.op === 'removeConnection' ||
					p?.op === 'removeElementFromModel' ||
					p?.op === 'removeRelationshipFromModel' ||
					p?.op === 'updateElementLabel' ||
					p?.op === 'updateConnectionLabel'
			);
			if (hasModelLevelChange) syncViews();
		};
		const unsubscribe = modeler.setupPatchSync?.(wrappedOnApplyPatch, {
			onLive,
			onLock,
			clientSessionId,
		});
		patchSyncUnsubRef.current = unsubscribe;
		return () => {
			patchSyncUnsubRef.current = null;
			unsubscribe?.();
		};
	}, [viewReady, onApplyPatch, onLive, onLock, clientSessionId, syncViews]);

	const applyRemotePatch = React.useCallback(
		patch => {
			const modeler = modelerRef.current;
			if (!modeler) return;
			applyingRemotePatchRef.current = true;
			try {
				modeler.applyModelPatch?.(patch);
				const hasModelLevelChange = patch?.patches?.some(
					p =>
						p?.op === 'addView' ||
						p?.op === 'updateViewName' ||
						p?.op === 'removeView' ||
						p?.op === 'addNode' ||
						p?.op === 'removeNode' ||
						p?.op === 'addConnection' ||
						p?.op === 'removeConnection' ||
						p?.op === 'removeElementFromModel' ||
						p?.op === 'removeRelationshipFromModel' ||
						p?.op === 'updateElementLabel' ||
						p?.op === 'updateConnectionLabel'
				);
				if (hasModelLevelChange) syncViews();
			} catch (e) {
				console.warn('[ArchiX6] applyRemotePatch failed:', e);
			} finally {
				setTimeout(() => {
					applyingRemotePatchRef.current = false;
				}, 450);
			}
		},
		[syncViews]
	);

	const applyVersionNavigate = React.useCallback(
		payload => {
			const modeler = modelerRef.current;
			if (!modeler || !payload?.modelJson) return;
			applyingRemotePatchRef.current = true;
			const viewId = modeler.getActiveViewId?.();
			let json = payload.modelJson;
			if (typeof json === 'string') {
				try {
					json = JSON.parse(json);
				} catch (e) {
					console.warn('[ArchiX6] applyVersionNavigate parse failed', e);
					applyingRemotePatchRef.current = false;
					return;
				}
			}
			modeler
				.importJSON(json)
				.then(() => modeler.openView(viewId ?? undefined))
				.then(() => {
					syncViews();
					modeler.cleanGraphHistory?.();
					setViewReady(true);
				})
				.catch(err => {
					console.warn('[ArchiX6] applyVersionNavigate failed:', err);
					void message.error('Не удалось применить версию диаграммы');
				})
				.finally(() => {
					setTimeout(() => {
						applyingRemotePatchRef.current = false;
					}, 450);
				});
		},
		[syncViews]
	);

	const applyLiveGeometry = React.useCallback(live => {
		modelerRef.current?.applyLiveGeometry?.(live);
	}, []);

	const applyLockEvent = React.useCallback(lock => {
		modelerRef.current?.applyLockEvent?.(lock);
	}, []);

	const applyCatalogChange = React.useCallback(
		changes => {
			const modeler = modelerRef.current;
			if (!modeler || !changes?.length) return;
			applyingRemotePatchRef.current = true;
			try {
				modeler.applyCatalogChange?.(changes);
				syncViews();
			} catch (e) {
				console.warn('[ArchiX6] applyCatalogChange failed:', e);
			} finally {
				setTimeout(() => {
					applyingRemotePatchRef.current = false;
				}, 450);
			}
		},
		[syncViews]
	);

	return {
		diagramContainerRef,
		minimapContainerRef,
		modelerRef,
		modelerReady,
		viewReady,
		views,
		activeViewId,
		setActiveViewId,
		modelContent,
		syncViews,
		applyRemotePatch,
		applyVersionNavigate,
		applyCatalogChange,
		applyLiveGeometry,
		applyLockEvent,
	};
}
